import { Flag } from 'lucide-react';
import { DistanceUnit, Language, RunLogEntry } from '../types';
import { formatPace, kmPaceToMilePace } from '../utils/calculator';
import { RaceGoal, goalSummary } from '../utils/goal';
import { t } from '../utils/i18n';
import { daysLeftText, goalDistanceName, statusColor, statusText } from './GoalCard';

interface GoalBannerProps {
  language: Language;
  goal: RaceGoal | null;
  logs: RunLogEntry[];
  distanceUnit: DistanceUnit;
  onOpen: () => void;
}

/** Compacte voortgangsbalk op het hoofdscherm; verschijnt alleen als er een racedoel is. */
export default function GoalBanner({ language, goal, logs, distanceUnit, onOpen }: GoalBannerProps) {
  if (!goal) return null;
  const summary = goalSummary(goal, logs);
  const pace = distanceUnit === 'mi' ? kmPaceToMilePace(summary.requiredPace) : summary.requiredPace;

  return (
    <button
      id="btn-goal-banner"
      onClick={onOpen}
      aria-label={t(language, 'goalTitle')}
      className="w-full flex items-center gap-3 text-left rounded-3xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-98 transition-all duration-150 p-4 cursor-pointer"
    >
      <span className="w-11 h-11 shrink-0 rounded-2xl bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 flex items-center justify-center">
        <Flag className="w-5 h-5 text-[#7C3AED] dark:text-[#A78BFA]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-bold text-slate-900 dark:text-white">
          {goalDistanceName(language, goal.distanceKm)} · {daysLeftText(language, summary)}
        </span>
        <span className={`block text-sm font-semibold ${statusColor(summary.status)}`}>{statusText(language, summary)}</span>
      </span>
      <span className="text-right shrink-0">
        <span className="block text-base font-bold text-slate-900 dark:text-white whitespace-nowrap">
          {formatPace(pace).replace(/^0/, '')} /{distanceUnit}
        </span>
        <span className="block text-sm text-slate-600 dark:text-slate-400">{t(language, 'goalRequiredPace')}</span>
      </span>
    </button>
  );
}
